import { createStyles, Theme, WithStyles, withStyles } from "@material-ui/core/styles";
import classNames from "classnames";
import colorString from "color-string";
import {
  convertFromHTML,
  convertToHTML,
  IConvertToHTMLConfig,
  IConvertFromHTMLConfig as OriginalIConvertFromHTMLConfig,
} from "draft-convert";
import { convertFromRaw, convertToRaw, DraftInlineStyleType, EditorState, Modifier, RichUtils } from "draft-js";
import { OrderedSet } from "immutable";
import MUIRichTextEditor from "mui-rte";
import { TCustomControl } from "mui-rte/src/components/Toolbar";
import { TMUIRichTextEditorRef } from "mui-rte/src/MUIRichTextEditor";
import React from "react";
import { SetColorToolbarButton } from "./SetColorToolbarButton";

// Style "input area" as per https://github.com/mui-org/material-ui/blob/next/packages/material-ui/src/Input/Input.js
const styles = (theme: Theme) => {
  const light = theme.palette.type === "light";
  const bottomLineColor = light ? "rgba(0, 0, 0, 0.42)" : "rgba(255, 255, 255, 0.7)";

  return createStyles({
    root: {
      marginTop: theme.spacing(2),
    },
    container: {},
    toolbar: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.background.paper,
    },
    editor: {},
    editorContainer: {
      marginTop: theme.spacing(1),
      "& .DraftEditor-root": {
        marginBottom: -theme.spacing(1),
      },
      "& .public-DraftStyleDefault-block": {
        margin: `${theme.spacing(1)}px 0`,
      },
      "& ol, & ul": {
        margin: `${theme.spacing(1)}px 0`,
        "& li": {
          marginBottom: theme.spacing(0.5),
          "& > .public-DraftStyleDefault-block": {
            margin: 0,
          },
        },
      },
      "&:after": {
        borderBottom: `2px solid ${theme.palette.primary.main}`,
        left: 0,
        bottom: 0,
        // Doing the other way around crash on IE 11 "''" https://github.com/cssinjs/jss/issues/242
        content: '""',
        position: "absolute",
        right: 0,
        transform: "scaleX(0)",
        transition: theme.transitions.create("transform", {
          duration: theme.transitions.duration.shorter,
          easing: theme.transitions.easing.easeOut,
        }),
        pointerEvents: "none", // Transparent to the hover style.
      },
      "&$focused:after": {
        transform: "scaleX(1)",
      },
      "&$error:after": {
        borderBottomColor: theme.palette.error.main,
        transform: "scaleX(1)", // error is always underlined in red
      },
      "&:before": {
        borderBottom: `1px solid ${bottomLineColor}`,
        left: 0,
        bottom: 0,
        // Doing the other way around crash on IE 11 "''" https://github.com/cssinjs/jss/issues/242
        content: '"\\00a0"',
        position: "absolute",
        right: 0,
        transition: theme.transitions.create("border-bottom-color", {
          duration: theme.transitions.duration.shorter,
        }),
        pointerEvents: "none", // Transparent to the hover style.
      },
      "&:hover:not($disabled):before": {
        borderBottom: `2px solid ${theme.palette.text.primary}`,
        // Reset on touch devices, it doesn't add specificity
        "@media (hover: none)": {
          borderBottom: `1px solid ${bottomLineColor}`,
        },
      },
      "&$disabled:before": {
        borderBottomStyle: "dotted",
      },
    },
    hidePlaceholder: {
      // Seems the global MUIRichTextEditor theme is overriding if we don't use important
      display: "block !important",
    },
    focused: {},
    disabled: {},
    /* Pseudo-class applied to the root element if `error={true}`. */
    error: {},
  });
};

const DefaultInlineColorStyles = [
  { name: "grey", color: "#757575" },
  { name: "red", color: "#f44336" },
  { name: "orange", color: "#ff9800" },
  { name: "green", color: "#4caf50" },
  { name: "blue", color: "#2196f3" },
  { name: "violet", color: "#673ab7" },
];

interface IConvertFromHTMLConfig extends Omit<OriginalIConvertFromHTMLConfig, "htmlToStyle"> {
  htmlToStyle?: <T extends DraftInlineStyleType>(
    nodeName: string,
    node: HTMLElement,
    inlineStyle: OrderedSet<T>,
  ) => OrderedSet<T>;
}

interface ConvertFromHTMLProcessedInlineStylesResult {
  inlineColorStyles: { [inlineColorStyleName: string]: string };
  inlineBackgroundColorStyles: { [inlineBackgroundColorStyleName: string]: string };
}

interface RichTextEditorProps extends WithStyles<typeof styles> {
  id: string;
  maxLength?: number;
  placeholder?: string;
  htmlContent?: string | null;
  enableFontColor?: boolean;
  className?: string;
  processHtmlContent?: (htmlContent: string | null | undefined) => string | null | undefined;
  onChange: (content: string | null | undefined) => void;
}

interface RichTextEditorState {
  defaultStringifiedRawContentState: string;
  hasContent: boolean;
  controls: string[];
  customControls: TCustomControl[];
  inlineColorStyles: Array<{ name: string; color: string }> | undefined;
}

export const RichTextEditorComponent = class extends React.Component<RichTextEditorProps, RichTextEditorState> {
  public muiRichTextEditorRef: React.RefObject<TMUIRichTextEditorRef>;
  public convertFromHtmlConfig: IConvertFromHTMLConfig;
  public convertToHtmlConfig: IConvertToHTMLConfig;

  constructor(props: RichTextEditorProps) {
    super(props);

    const { htmlContent } = this.props;

    const convertFromHtmlProcessedInlineStylesResult: ConvertFromHTMLProcessedInlineStylesResult = {
      inlineColorStyles: {},
      inlineBackgroundColorStyles: {},
    };

    this.muiRichTextEditorRef = React.createRef<TMUIRichTextEditorRef>();
    this.convertFromHtmlConfig = this.getConvertFromHtmlConfig(convertFromHtmlProcessedInlineStylesResult);
    this.convertToHtmlConfig = this.getConvertToHtmlConfig();

    const contentState = htmlContent
      ? convertFromHTML(this.convertFromHtmlConfig as any)(htmlContent)
      : EditorState.createEmpty().getCurrentContent();
    const defaultStringifiedRawContentState = JSON.stringify(convertToRaw(contentState));

    const { controls, customControls, inlineColorStyles } = this.getRichTextEditorConfiguration(
      convertFromHtmlProcessedInlineStylesResult,
    );

    this.state = {
      defaultStringifiedRawContentState,
      controls,
      customControls,
      inlineColorStyles,
      hasContent: contentState.hasText(),
    };
  }

  render() {
    const { id, maxLength, placeholder, className, classes } = this.props;
    const { defaultStringifiedRawContentState, controls, customControls } = this.state;

    return (
      <MUIRichTextEditor
        id={id}
        ref={this.muiRichTextEditorRef}
        maxLength={maxLength}
        label={placeholder || "Start typing..."}
        controls={controls}
        customControls={customControls}
        defaultValue={defaultStringifiedRawContentState}
        onChange={this.handleChange}
        onBlur={this.handleBlur}
        onSave={this.handleSave}
        classes={{
          root: classNames(classes.root, className),
          container: classes.container,
          toolbar: classes.toolbar,
          editor: classes.editor,
          editorContainer: classes.editorContainer,
          hidePlaceholder: classes.hidePlaceholder,
        }}
      />
    );
  }

  public componentDidUpdate(prevProps: RichTextEditorProps, prevState: RichTextEditorState) {
    const { htmlContent: prevHtmlContent } = prevProps;
    const { htmlContent } = this.props;

    if (prevHtmlContent !== htmlContent) {
      const convertFromHtmlProcessedInlineStylesResult: ConvertFromHTMLProcessedInlineStylesResult = {
        inlineColorStyles: {},
        inlineBackgroundColorStyles: {},
      };

      this.convertFromHtmlConfig = this.getConvertFromHtmlConfig(convertFromHtmlProcessedInlineStylesResult);

      const contentState = htmlContent
        ? convertFromHTML(this.convertFromHtmlConfig as any)(htmlContent)
        : EditorState.createEmpty().getCurrentContent();
      const defaultStringifiedRawContentState = JSON.stringify(convertToRaw(contentState));

      const { controls, customControls, inlineColorStyles } = this.getRichTextEditorConfiguration(
        convertFromHtmlProcessedInlineStylesResult,
      );

      this.setState({
        defaultStringifiedRawContentState,
        controls,
        customControls,
        inlineColorStyles,
        hasContent: contentState.hasText(),
      });
    }
  }

  public handleChange = (editorState: EditorState) => {
    const { hasContent } = this.state;

    const nextHasContent = editorState.getCurrentContent().hasText();

    // If the Content State has changed...
    if (hasContent !== nextHasContent) {
      this.setState({ ...this.state, hasContent: nextHasContent });
    }
  };

  public handleBlur = () => {
    this.muiRichTextEditorRef.current?.save();
  };

  public handleSave = (stringifiedRawContentState: string) => {
    const { onChange, processHtmlContent } = this.props;
    const { hasContent } = this.state;

    let htmlContent: string | undefined;

    if (hasContent) {
      const contentState = convertFromRaw(JSON.parse(stringifiedRawContentState));
      htmlContent = convertToHTML(this.convertToHtmlConfig)(contentState);
    }

    const processedHtmlContent = processHtmlContent
      ? (processHtmlContent(htmlContent) as string | undefined | null)
      : typeof htmlContent === "string"
      ? htmlContent.trim()
      : htmlContent;

    onChange(processedHtmlContent);
  };

  public handleSetColorClick = (editorState: EditorState, name: string, anchor: HTMLElement | null) => {
    const { inlineColorStyles } = this.state;

    if (!inlineColorStyles) {
      throw Error("Inline Color Styles not configured.");
    }

    const selection = editorState.getSelection();

    // Let's just allow one color at a time. Turn off all active colors.
    const nextContentState = inlineColorStyles
      .map(s => this.getColorStyleName(s.color))
      .reduce((contentState, color) => {
        return Modifier.removeInlineStyle(contentState, selection, color);
      }, editorState.getCurrentContent());

    let nextEditorState = EditorState.push(editorState, nextContentState, "change-inline-style") as EditorState;

    const currentStyle = editorState.getCurrentInlineStyle();

    // Unset style override for current color.
    if (selection.isCollapsed()) {
      nextEditorState = currentStyle.reduce((state, color) => {
        if (!(state && color)) {
          throw Error("state and color were unexpectedly not available.");
        }
        if (color.indexOf("COLOR-") === 0) {
          return RichUtils.toggleInlineStyle(state, color);
        }
        return state;
      }, nextEditorState);
    }

    const colorNameResult = /color-(.*)/.exec(name);

    if (!colorNameResult) {
      throw Error("Set Color Control name is not in correct format.");
    }

    const colorStyleName = this.getColorStyleName(colorNameResult[1]);

    // If the color is being toggled on, apply it.
    if (!currentStyle.has(colorStyleName)) {
      nextEditorState = RichUtils.toggleInlineStyle(nextEditorState, colorStyleName);
    }

    return nextEditorState;
  };

  public getColorStyleName = (color: string) => `COLOR-${color.toUpperCase()}`;

  public getHighlightStyleName = (backgroundColor: string) => `HIGHLIGHT-${backgroundColor.toUpperCase()}`;

  public getConvertFromHtmlConfig = (processedInlineStylesResult: ConvertFromHTMLProcessedInlineStylesResult) => {
    const convertFromHtmlConfig: IConvertFromHTMLConfig = {
      htmlToStyle: (nodeName, node, inlineStyle: OrderedSet<string>): any => {
        if (nodeName === "span") {
          // If an inline color style...
          if (node.style.color) {
            let colorStyleName: string | undefined;
            // If this is a keyword color...
            if (/^[^\d]+$/.test(node.style.color)) {
              colorStyleName = this.getColorStyleName(node.style.color);

              processedInlineStylesResult.inlineColorStyles[colorStyleName] = node.style.color;
            }
            // Otherwise, this is some other color representation...
            else {
              const color = colorString.get(node.style.color);

              if (!color) {
                throw Error("Could not parse color.");
              }

              const colorHex = colorString.to.hex(color.value);

              colorStyleName = this.getColorStyleName(colorHex);

              processedInlineStylesResult.inlineColorStyles[colorStyleName] = colorHex;
            }

            return inlineStyle.add(colorStyleName);
          }
          // Otherwise, if an inline highlight style...
          else if (node.style.backgroundColor) {
            // If the built-in inline highlight style...
            if (node.style.backgroundColor === "yellow") {
              return inlineStyle.add("HIGHLIGHT");
            }

            let backgroundColorStyleName: string | undefined;
            // If this is a keyword color...
            if (/^[^\d]+$/.test(node.style.color)) {
              backgroundColorStyleName = this.getColorStyleName(node.style.color);

              processedInlineStylesResult.inlineBackgroundColorStyles[backgroundColorStyleName] = node.style.color;
            }
            // Otherwise, this is some other color representation...
            else {
              const backgroundColor = colorString.get(node.style.backgroundColor);

              if (!backgroundColor) {
                throw Error("Could not parse Background Color.");
              }

              const backgroundColorHex = colorString.to.hex(backgroundColor.value);

              backgroundColorStyleName = this.getColorStyleName(backgroundColorHex);

              processedInlineStylesResult.inlineBackgroundColorStyles[backgroundColorStyleName] = backgroundColorHex;
            }

            return inlineStyle.add(backgroundColorStyleName);
          } else {
            return inlineStyle;
          }
        } else {
          return inlineStyle;
        }
      },
    };

    return convertFromHtmlConfig;
  };

  public getConvertToHtmlConfig = () => {
    const convertToHtmlConfig: IConvertToHTMLConfig = {
      styleToHTML: (style: string) => {
        // If a color inline style...
        if (style.indexOf("COLOR-") === 0) {
          // Color is embedded within inline style name in the format COLOR-[color]
          const colorResult = /COLOR-(.*)/.exec(style);

          if (!colorResult) {
            throw Error("COLOR inline style was not in a supported format.");
          }

          const color = colorResult[1].toLowerCase();

          return <span style={{ color }} />;
        }
        // Otherwise, if a highlight inline style...
        else if (style.indexOf("HIGHLIGHT-") === 0) {
          // Background color is embedded within inline style name in the format HIGHLIGHT-[backgroundColor]
          const backgroundColorResult = /HIGHLIGHT-(.*)/.exec(style);

          if (!backgroundColorResult) {
            throw Error("HIGHLIGHT inline style was not in a supported format.");
          }

          const backgroundColor = backgroundColorResult[1].toLowerCase();

          return <span style={{ backgroundColor }} />;
        }
        // Otherwise, if the built-in highlight style...
        else if (style === "HIGHLIGHT") {
          return <span style={{ backgroundColor: "yellow" }} />;
        }
        return undefined;
      },
    };

    return convertToHtmlConfig;
  };

  public getRichTextEditorConfiguration = (
    convertFromHtmlProcessedInlineStylesResult: ConvertFromHTMLProcessedInlineStylesResult,
  ) => {
    const { enableFontColor } = this.props;

    const { inlineColorStyles: processedInlineColorStyles } = convertFromHtmlProcessedInlineStylesResult;

    const customControls: TCustomControl[] = [];
    const customControlNames: string[] = [];

    let inlineColorStyles: Array<{ name: string; color: string }> | undefined;

    if (enableFontColor) {
      inlineColorStyles = DefaultInlineColorStyles;

      inlineColorStyles.forEach(inlineColorStyle => {
        const colorStyleControlName = `color-${inlineColorStyle.color}`;
        // Add the control that may be selected to set the color...
        customControls.push({
          id: `color-${inlineColorStyle.color}`,
          name: colorStyleControlName,
          type: "inline-callback",
          inlineStyle: {
            color: inlineColorStyle.color,
          },
          component: SetColorToolbarButton,
          // TODO: Remove any cast
          onClick: this.handleSetColorClick as any,
        });
        // Enable the callback control...
        customControlNames.push(colorStyleControlName);
      });

      const unrecognizedInlineColorStyleNames = Object.keys(processedInlineColorStyles).filter(
        inlineColorStyleName =>
          !customControls.map(cc => cc.name.toUpperCase()).includes(inlineColorStyleName.toUpperCase()),
      );

      // For all unrecognized styles, add an inline style so that the element doesn't go unstyled...
      unrecognizedInlineColorStyleNames.forEach(unrecognizedInlineColorStyleName => {
        customControls.push({
          name: unrecognizedInlineColorStyleName.toUpperCase(),
          type: "inline",
          inlineStyle: {
            color: processedInlineColorStyles[unrecognizedInlineColorStyleName],
          },
        });
      });
    }

    const controls = [
      "bold",
      "italic",
      "underline",
      ...customControlNames,
      "highlight",
      "numberList",
      "bulletList",
      "undo",
      "redo",
      "clear",
    ];

    return {
      controls,
      customControls,
      inlineColorStyles,
    };
  };
};

export const RichTextEditor = withStyles(styles)(RichTextEditorComponent);
